/**
 * Password validation utilities
 * Validates password requirements and provides real-time feedback
 */

export interface PasswordRequirements {
  hasNumber: boolean;
  hasSpecialChar: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasMinLength: boolean;
  allRequirementsMet: boolean;
}

// Special characters as specified: ^ $ * . [ ] { } ( ) ? - " ! @ # % & / \ , > < ' : ; | _ ~ ` + =
export const SPECIAL_CHARS_LIST = "^ $ * . [ ] { } ( ) ? - \" ! @ # % & / \\ , > < ' : ; | _ ~ ` + =";
const SPECIAL_CHARS = /[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]/;
const HAS_NUMBER = /[0-9]/;
const HAS_UPPERCASE = /[A-Z]/;
const HAS_LOWERCASE = /[a-z]/;
const MIN_LENGTH = 8;

/**
 * Validates password against all security requirements
 * @param password - The password string to validate
 * @returns Object containing validation status for each requirement
 */
export function validatePassword(password: string): PasswordRequirements {
  const hasNumber = HAS_NUMBER.test(password);
  const hasSpecialChar = SPECIAL_CHARS.test(password);
  const hasUpperCase = HAS_UPPERCASE.test(password);
  const hasLowerCase = HAS_LOWERCASE.test(password);
  const hasMinLength = password.length >= MIN_LENGTH;

  return {
    hasNumber,
    hasSpecialChar,
    hasUpperCase,
    hasLowerCase,
    hasMinLength,
    allRequirementsMet: hasNumber && hasSpecialChar && hasUpperCase && hasLowerCase && hasMinLength,
  };
}

/**
 * Checks if two passwords match
 * @param password - The original password
 * @param confirmPassword - The confirmation password
 * @returns True if passwords match and are not empty
 */
export function passwordsMatch(password: string, confirmPassword: string): boolean {
  return password !== '' && password === confirmPassword;
}
